import { logger } from '../useSessionRecordingStore/lib/logger';
import { dbV2 } from './dbV2';
import { packBlob, unpackBlob } from './packBlob';
import { PersistenceUnavailableError } from './persistenceUnavailableError';
import { chunkPk } from './types';
import { assertWriteAllowed, captureEpoch } from './writeFence';

export { PersistenceUnavailableError };

const OPEN_TIMEOUT_MS = 10_000;

const CANARY_KEY = { sessionId: '__canary__', recordingId: '__canary__', index: 0 };
const CANARY_BYTES = 64 * 1024;

const CORRUPTION_STRIKES_KEY = 'heidi-audio-ledger-open-corrupted-strikes';

let corruptionLoggedThisLoad = false;
let persistRequested = false;
let handlersInstalled = false;

const readCorruptionStrikes = () => {
  try {
    return Number(window.localStorage.getItem(CORRUPTION_STRIKES_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeCorruptionStrikes = (strikes: number) => {
  try {
    if (strikes <= 0) window.localStorage.removeItem(CORRUPTION_STRIKES_KEY);
    else window.localStorage.setItem(CORRUPTION_STRIKES_KEY, String(strikes));
  } catch {
    // Strike telemetry is best effort.
  }
};

const storageEstimate = async () => {
  try {
    const { usage, quota } = (await navigator.storage.estimate()) ?? {};
    return { usage, quota };
  } catch {
    return {};
  }
};

export const installLifecycleHandlers = () => {
  if (handlersInstalled) return;
  handlersInstalled = true;

  dbV2.on('blocked', () => {
    logger.warn({ event_type: 'LEDGER_OPEN_BLOCKED' });
  });

  // Subscribing replaces Dexie's default handler, so close explicitly.
  dbV2.on('versionchange', () => {
    logger.warn({ event_type: 'LEDGER_VERSION_CHANGE' });
    dbV2.close({ disableAutoOpen: false });
  });

  dbV2.on('close', () => {
    logger.warn({ event_type: 'LEDGER_CONNECTION_CLOSED' });
  });
};

const openWithTimeout = async () => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new PersistenceUnavailableError(
            `v2 db open exceeded ${OPEN_TIMEOUT_MS}ms (blocked by a frozen tab?)`
          )
        ),
      OPEN_TIMEOUT_MS
    );
  });
  const open = dbV2.open();
  open.catch(() => undefined); // Observe a late rejection after timeout.
  try {
    await Promise.race([open, timeout]);
  } finally {
    clearTimeout(timer);
  }
};

/** Opens within a bounded wait and reports repeated corruption without deleting data. */
export const ensureOpen = async () => {
  installLifecycleHandlers();
  if (dbV2.isOpen()) return;

  try {
    await openWithTimeout();
    if (readCorruptionStrikes() > 0) writeCorruptionStrikes(0);
  } catch (err) {
    const name = err instanceof Error ? err.name : '';
    if (name === 'UnknownError' && !corruptionLoggedThisLoad) {
      corruptionLoggedThisLoad = true;
      const consecutiveLoads = readCorruptionStrikes() + 1;
      writeCorruptionStrikes(consecutiveLoads);
      logger.error({
        event_type: 'LEDGER_OPEN_CORRUPTED',
        details: {
          error: err instanceof Error ? err.message : String(err),
          consecutiveLoads,
          ...(await storageEstimate()),
        },
      });
    }
    throw err;
  }
};

/**
 * Reopens and retries once on a transient connection fault:
 *  - `DatabaseClosedError`: the connection died mid-op.
 *  - `NotFoundError`: WebKit can keep a connection open with a stale schema
 *    view, so it needs a forced close+reopen. A recurrence is genuine drift.
 */
export const withReopen = async <T>(op: () => Promise<T>): Promise<T> => {
  await ensureOpen();
  try {
    return await op();
  } catch (err) {
    if (!(err instanceof Error)) throw err;
    if (err.name === 'DatabaseClosedError') {
      logger.warn({ event_type: 'LEDGER_REOPEN_RETRY' });
      await ensureOpen();
      return op();
    }
    if (err.name === 'NotFoundError') {
      logger.warn({ event_type: 'LEDGER_STALE_SCHEMA_REOPEN_RETRY' });
      dbV2.close({ disableAutoOpen: false });
      await ensureOpen();
      return op();
    }
    throw err;
  }
};

/** Requests persistent origin storage and logs whether the browser grants it. */
export const requestPersistentStorage = async () => {
  if (persistRequested) return;
  persistRequested = true;
  try {
    if (typeof navigator.storage?.persist !== 'function') {
      logger.info({ event_type: 'STORAGE_PERSIST_UNSUPPORTED' });
      return;
    }
    const granted = await navigator.storage.persist();
    logger.info({ event_type: 'STORAGE_PERSIST_RESULT', details: { granted } });
  } catch (err) {
    logger.warn({
      event_type: 'STORAGE_PERSIST_ERROR',
      details: { error: err instanceof Error ? err.message : String(err) },
    });
  }
};

/** Verifies IndexedDB writability with a fixed 64 KB sentinel key. */
export const runCanary = async () => {
  const epoch = captureEpoch();
  await withReopen(async () => {
    assertWriteAllowed(epoch);
    const blob = new Blob([new ArrayBuffer(CANARY_BYTES)], {
      type: 'application/octet-stream',
    });
    const stored = await packBlob(blob);
    await dbV2.chunkBlobs.put({ ...CANARY_KEY, blob: stored });

    const readBack = await dbV2.chunkBlobs.get(chunkPk(CANARY_KEY));
    if (!readBack || unpackBlob(readBack.blob).size !== CANARY_BYTES) {
      throw new Error('v2 canary read-back mismatch');
    }
  });

  try {
    await dbV2.chunkBlobs.delete(chunkPk(CANARY_KEY));
  } catch (err) {
    logger.warn({
      event_type: 'LEDGER_CANARY_DELETE_FAILED',
      details: { error: err instanceof Error ? err.message : String(err) },
    });
  }
};

/** Resets module guards for tests. */
export const __resetLifecycleForTests = () => {
  corruptionLoggedThisLoad = false;
  persistRequested = false;
  handlersInstalled = false;
};
